import { Fragment, useState } from 'react';

import { CircleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { type MyRoleGrant } from '@/models/permission';
import { type User } from '@/models/user';

import { ScopePicker, useGrantScope } from './ScopePicker';
import { useRoles } from '../api/getRoles';

interface GrantRoleDialogProps {
  member: User;
  /** Every Grant the user already holds, as the sheet read them back. */
  grants: MyRoleGrant[];
  gameCode: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (grant: { role_id: string; sandbox_code: string }) => void;
  isPending: boolean;
}

/**
 * Adds one Role Grant: a Standard Role plus the scope it applies on. The scope
 * offered is capped by the viewer's own — a Sandbox-scoped manager cannot grant
 * on All Sandboxes, nor on a Sandbox they do not administer.
 */
export const GrantRoleDialog = ({
  member,
  grants,
  gameCode,
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: GrantRoleDialogProps) => {
  const { data: rolesData } = useRoles();
  const grantScope = useGrantScope(gameCode);
  const [roleId, setRoleId] = useState('');

  const roles = rolesData?.roles ?? [];
  const targetScope = grantScope.scope;
  // The radio carries the Role id; the taken-check needs the name.
  const selectedRoleName = roles.find((role) => role.id === roleId)?.name ?? '';

  // A Role can be granted once per scope, so an existing Grant on the selected
  // scope rules that Role out. Matched on the Role name, the only key a Grant
  // carries — it has no role id. Re-granting is a server-side no-op anyway; this
  // just says so before the click rather than after.
  const isTaken = (name: string) =>
    grants.some(
      (grant) =>
        grant.role === name &&
        grant.game_code === gameCode &&
        grant.sandbox_code === targetScope,
    );

  const submit = () => {
    onSubmit({ role_id: roleId, sandbox_code: targetScope });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-140">
        <DialogHeader>
          <DialogTitle>Add a Role Grant</DialogTitle>
          <DialogDescription>
            For {member.email}. A Grant is one Standard Role plus the scope it
            applies on.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="grid gap-2">
            <Label>Standard Role</Label>
            <RadioGroup
              value={roleId}
              onValueChange={setRoleId}
              className="max-h-64 gap-1.5 overflow-y-auto pr-1"
            >
              {roles.map((role) => {
                const taken = isTaken(role.name);

                const option = (
                  <Label
                    className={cn(
                      'flex items-start gap-3 rounded-md border p-3 text-sm font-normal',
                      taken && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    <RadioGroupItem value={role.id} disabled={taken} />
                    <span className="font-medium">{role.name}</span>
                    <span className="min-w-0 flex-1 text-xs text-muted-foreground">
                      {role.description}
                    </span>
                  </Label>
                );

                // The row carries the tooltip, not the radio: a disabled input
                // fires no pointer events.
                return taken ? (
                  <Tooltip key={role.id}>
                    <TooltipTrigger asChild>{option}</TooltipTrigger>
                    <TooltipContent>
                      Already granted on this scope
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Fragment key={role.id}>{option}</Fragment>
                );
              })}
            </RadioGroup>
          </div>

          <ScopePicker label="Scope of this Grant" {...grantScope} />

          {selectedRoleName !== '' && isTaken(selectedRoleName) && (
            <span className="flex items-center gap-2 text-xs text-destructive">
              <CircleAlert size={14} />
              This user already holds that Role on this scope. A Role can be
              granted once per scope.
            </span>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              roleId === '' ||
              isTaken(selectedRoleName) ||
              targetScope === '' ||
              isPending
            }
          >
            Add Grant
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
